import { bareAddress, decorate, forwardBody, prefixSubject, quoteBody, replyRecipients } from '../../lib/mail.js'
import { fullDate } from '../../lib/time.js'
import { printDocument, printHtml } from '../../lib/print.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-reader ---
let __fez_component_0;
{
// A conversation longer than this shows its first message, a fold, and the last two.
const FOLD = 4
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: String,
    thread: String,
    email: String,
    back_href: String,
    tick: Number,
    senders: Object,
    addresses: Array,
    can_delete: Boolean,
    labels: Array,
    sources: Array,
    on_labels: { type: Function, state: true, default: () => {} },
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.subject = ''
    this.state.items = []
    this.state.opened = {}
    this.state.full = {}
    this.state.known = {}
    this.state.unfolded = false
    this.state.reply = null
    this.state.printing = false
    this.state.screening = false
    this.state.team = null
    this.state.people = []
    this.state.peopleFor = ''
    this.state.seq = 0
  }

  onRefresh(props, changed) {
    if ('thread' in changed || 'email' in changed) this.load(false)
    else if ('tick' in changed) this.load(true)
  }

  api(action, params = {}, options = { silent: true }) {
    return window.App.api(`container/${this.props.container}/${action}`, { mailbox: this.props.mailbox || undefined, ...params }, options)
  }

  // `keep` is a live reload: open messages stay open and new ones open.
  // Another conversation keeps the current one on screen until everything it shows has loaded, then swaps in one render.
  async load(keep) {
    const seq = ++this.state.seq
    try {
      const [view, team] = await Promise.all([this.fetchView(), this.fetchTeam()])
      const items = view.items
      const opened = keep ? { ...this.state.opened } : {}
      for (const item of items) {
        if (!item.seen || (keep && !this.state.known[item.id])) opened[item.id] = true
      }
      const last = items[items.length - 1]
      if (last && (!keep || !this.state.known[last.id])) opened[last.id] = true
      const full = keep ? { ...this.state.full, ...view.full } : view.full
      await Promise.all(Object.keys(opened).filter((id) => !full[id]).map(async (id) => {
        full[id] = await this.api('email', { email: id })
      }))
      if (seq !== this.state.seq) return
      if (!keep) {
        this.state.reply = null
        this.state.unfolded = false
      }
      this.state.known = Object.fromEntries(items.map((item) => [item.id, true]))
      this.state.items = items
      this.state.subject = view.subject
      this.state.opened = opened
      this.state.full = full
      this.state.team = team
      this.state.error = ''
      if (!keep && !this.state.loading) this.root.scrollIntoView({ block: 'start' })
      this.markSeen(items.filter((item) => !item.seen))
    } catch (error) {
      if (seq === this.state.seq) this.state.error = error.code === 'not_found' ? 'This conversation is gone. It was deleted or moved out of reach.' : error.message
    } finally {
      if (seq === this.state.seq) this.state.loading = false
    }
  }

  // The conversation, or the one email, with the full views it already fetched.
  async fetchView() {
    if (this.props.thread) {
      const view = await this.api('thread', { thread: this.props.thread })
      return { items: view.items, subject: view.subject, full: {} }
    }
    const email = await this.api('email', { email: this.props.email })
    return { items: [email], subject: email.subject, full: { [email.id]: email } }
  }

  // The conversation's assignee, status and activity, and who it can go to; conversations only.
  async fetchTeam() {
    if (!this.props.thread) return null
    try {
      const team = await this.api('thread_activity', { thread: this.props.thread })
      if (this.state.peopleFor !== team.mailbox) {
        const people = await this.api('assignees', { mailbox: team.mailbox })
        this.state.people = people.items
        this.state.peopleFor = team.mailbox
      }
      return team
    } catch {
      return null
    }
  }

  async loadTeam(seq) {
    const team = await this.fetchTeam()
    if (seq === this.state.seq) this.state.team = team
  }

  setTeam(view) {
    this.state.team = view
  }

  // The new sender question for the sender who started this conversation.
  async screen(decision) {
    const team = this.state.team
    if (!team?.sender) return
    this.state.screening = true
    try {
      await this.api('sender_move', { mailbox: team.mailbox, address: team.sender, decision })
      const done = { inbox: 'Their mail goes to the Inbox', feed: 'Their mail goes to the Feed', papertrail: 'Their mail goes to the Paper Trail', spam: 'Their mail goes to Spam' }
      window.Toast?.success(done[decision])
      if (decision === 'spam') this.next()
      else await this.loadTeam(this.state.seq)
    } catch (error) {
      window.Toast?.error(error.message)
    } finally {
      this.state.screening = false
    }
  }

  async loadFull(id) {
    if (this.state.full[id]) return this.state.full[id]
    const email = await this.api('email', { email: id })
    this.state.full = { ...this.state.full, [id]: email }
    return email
  }

  // A sender's images were trusted or forgotten: their loaded messages in that mailbox load again.
  async reloadSender(id) {
    const sender = this.state.full[id]
    const from = (email) => (email.from_addr || '').toLowerCase()
    const ids = Object.values(this.state.full).filter((email) => email.mailbox === sender.mailbox && from(email) === from(sender)).map((email) => email.id)
    const views = await Promise.all(ids.map((email) => this.api('email', { email })))
    this.state.full = { ...this.state.full, ...Object.fromEntries(views.map((view) => [view.id, view])) }
  }

  async markSeen(items) {
    if (!items.length || this.globalState.sudo) return
    const ids = items.map((item) => item.id)
    await this.api('mail_update', { emails: ids, seen: true }).catch(() => {})
    this.patch(ids, { seen: true })
  }

  patch(ids, change) {
    this.state.items = this.state.items.map((item) => (ids.includes(item.id) ? { ...item, ...change } : item))
    const full = { ...this.state.full }
    for (const id of ids) if (full[id]) full[id] = { ...full[id], ...change }
    this.state.full = full
  }

  async toggle(id) {
    this.state.opened = { ...this.state.opened, [id]: !this.state.opened[id] }
    if (this.state.opened[id]) await this.loadFull(id)
  }

  unfold() {
    this.state.unfolded = true
  }

  latest() {
    return this.state.items[this.state.items.length - 1]
  }

  async toggleStar(id) {
    const item = id ? this.state.items.find((email) => email.id === id) : this.latest()
    if (!item) return
    const state = await this.api('email_update', { email: item.id, flagged: !item.flagged })
    this.patch([item.id], state)
  }

  // Unread again and back to the list, so opening it does not read it at once.
  async markUnread() {
    const item = this.latest()
    if (!item) return
    await this.api('email_update', { email: item.id, seen: false })
    window.App.go(this.props.back_href)
  }

  // Moves every message of the conversation, then opens the next row (or the list).
  async moveTo(folder) {
    const items = this.state.items.filter((item) => item.folder !== folder)
    if (!items.length) return
    await this.api('mail_update', this.props.thread ? { threads: [this.props.thread], folder } : { emails: items.map((item) => item.id), folder })
    const label = { archive: 'Archived', trash: 'Moved to trash', inbox: 'Moved to inbox', sent: 'Moved to sent', spam: 'Marked as spam' }[folder]
    window.Toast?.success(label || 'Moved')
    this.next()
  }

  // Every message of the conversation, collapsed ones too, as View shows it.
  async print() {
    this.state.printing = true
    try {
      const messages = await Promise.all(this.state.items.map((item) => this.loadFull(item.id)))
      printHtml(printDocument(this.state.subject || '(no subject)', messages))
    } finally {
      this.state.printing = false
    }
  }

  async deleteForever() {
    const ok = await window.Dialog.confirm({ title: 'Delete for good?', text: 'The conversation cannot be brought back.', confirm: 'Delete', danger: true })
    if (!ok) return
    for (const item of this.state.items) await this.api('email_delete', { email: item.id })
    this.next()
  }

  next() {
    const before = window.location.hash
    document.querySelector('.mail-list-fez')?.fez?.step(1)
    if (window.location.hash === before) window.App.go(this.props.back_href)
  }

  // Opens the reply card for the latest message (keyboard) or the given one.
  async respond(kind, id) {
    const item = id ? this.state.items.find((email) => email.id === id) : this.latest()
    if (!item || !this.props.senders || !this.state.canReply) return
    const email = await this.loadFull(item.id)
    const when = fullDate(email.received_at)
    const domains = this.props.senders.domains.map((domain) => domain.name)
    // Answer from the address the mail arrived at, without its +tag.
    const local = bareAddress(email.address).split('@')[0]
    const base = { kind, for: email.id, mailbox: email.mailbox, domain: domains.includes(email.host) ? email.host : undefined, from_local: local }
    if (kind === 'forward') {
      const attachments = email.files
        .filter((file) => !file.url)
        .map((file) => ({ file: file.id, filename: file.filename, content_type: file.content_type, size: file.size }))
      this.state.reply = { ...base, to: '', cc: '', subject: prefixSubject('Fwd:', email.subject), body: forwardBody(email, when), attachments, in_reply_to: undefined }
    } else {
      this.state.reply = { ...base, ...replyRecipients(email, this.props.addresses, kind === 'all'), subject: prefixSubject('Re:', email.subject), body: quoteBody(email, when), attachments: [], in_reply_to: email.id }
    }
  }

  async replyDone(done) {
    this.state.reply = null
    if (done) await this.load(true)
  }

  beforeRender() {
    const items = this.state.items.map((item) => decorate({ ...item }))
    const folded = items.length > FOLD && !this.state.unfolded
    this.state.entries = items.flatMap((item, index) => {
      const middle = index > 0 && index < items.length - 2
      if (folded && middle && !this.state.opened[item.id]) {
        return index === 1 ? [{ fold: true, count: items.slice(1, -2).filter((other) => !this.state.opened[other.id]).length }] : []
      }
      return [{ item, full: this.state.full[item.id] || null, open: Boolean(this.state.opened[item.id]) }]
    })
    const folders = new Set(items.map((item) => item.folder))
    const only = folders.size === 1 ? [...folders][0] : ''
    this.state.folder = only
    const home = items.some((item) => item.direction === 'in') ? 'inbox' : 'sent'
    this.state.home = home
    this.state.canArchive = only !== 'archive'
    this.state.canTrash = only !== 'trash'
    this.state.canHome = Boolean(only) && only !== home
    this.state.canDelete = this.props.can_delete && (only === 'trash' || only === 'spam')
    this.state.countLabel = items.length > 1 ? `${items.length} messages` : ''
    this.state.replyTitle = this.state.reply ? { reply: 'Reply', all: 'Reply all', forward: 'Forward' }[this.state.reply.kind] : ''
    const latest = items[items.length - 1]
    const others = latest ? [...(latest.to || []), ...(latest.cc || [])].length : 0
    this.state.manyRecipients = others > 1
    // Members who only read the mailbox get no reply buttons.
    const box = latest && this.props.senders?.mailboxes.find((mailbox) => mailbox.ref === latest.mailbox)
    this.state.canReply = !box || box.can_send !== false
    const team = this.state.team
    this.state.waiting = Boolean(team?.sender) && team.category === 'new'
    const first = items.find((item) => item.direction === 'in' && item.from_addr?.toLowerCase() === team?.sender)
    this.state.screenWho = first?.from_name || team?.sender || ''
  }

  CSS = `:fez {
display:block;.read{max-width:920px;margin:0 auto;padding:var(--space-3) var(--space-6) var(--space-10)}.read-bar{position:sticky;top:0;z-index:1;display:flex;align-items:center;gap:var(--space-1);margin:0 calc(var(--space-6) * -1);padding:var(--space-2) var(--space-6);background:var(--color-bg)}.read-grow{flex:1}.read-subject{margin:var(--space-3) 0 var(--space-1);font-size:var(--text-xl);font-weight:var(--weight-bold);letter-spacing:-0.015em;line-height:var(--leading-tight);overflow-wrap:anywhere}.read-sub{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2);margin-bottom:var(--space-5);color:var(--color-text-subtle);font-size:var(--text-sm)}.read-folder{padding:1px var(--space-2);border-radius:var(--radius-full);background:var(--color-bg-subtle);color:var(--color-text-muted);font-size:var(--text-xs);font-weight:var(--weight-semibold);text-transform:capitalize}.read-screen{display:grid;gap:var(--space-3);margin-bottom:var(--space-4)}.read-screen-text{display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-sm);color:var(--color-text-muted)}.read-screen-who{color:var(--color-text);font-weight:var(--weight-semibold)}.read-screen-actions{display:flex;flex-wrap:wrap;gap:var(--space-2)}.read-thread{display:grid;grid-template-columns:minmax(0,1fr);gap:var(--space-3)}.read-fold{display:flex;align-items:center;gap:var(--space-3);width:100%;padding:var(--space-1) 0;border:0;background:transparent;color:var(--color-text-muted);font:inherit;font-size:var(--text-sm);font-weight:var(--weight-medium);cursor:pointer;&::before,&::after{content:'';flex:1;border-top:1px dashed var(--color-border-strong)}&:hover{color:var(--color-text)}}.read-respond{display:flex;flex-wrap:wrap;gap:var(--space-2);margin-top:var(--space-5)}.read-compose{margin-top:var(--space-5)}.read-compose-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--space-4)}.read-compose-title{font-size:var(--text-md);font-weight:var(--weight-semibold)}.read-state{padding:var(--space-12) var(--space-6)} :mobile {.read{padding:var(--space-2) var(--space-4) var(--space-8)}.read-bar{margin:0 calc(var(--space-4) * -1);padding:var(--space-2) var(--space-4)}.read-subject{font-size:var(--text-lg)}.read-screen-actions{display:grid;grid-template-columns:1fr 1fr}}
}`

  HTML = `{#if state.error}
<div class="read-state">
<ui-empty icon="warning-circle" title="Could not open this" text={state.error}>
<ui-button icon="arrow-left" label="Back to the list" href={props.back_href}></ui-button>
</ui-empty>
</div>
{:else if !state.loading}
<div class="read" key="read" fez:in="fly, x=16, duration=220">
<div class="read-bar">
<ui-button size="sm" variant="ghost" icon="arrow-left" title="Back to the list (u)" href={props.back_href}></ui-button>
<span class="read-grow"></span>
{#if state.canArchive}<ui-button size="sm" variant="ghost" icon="archive" title="Archive (e)" onclick="fez.moveTo('archive')"></ui-button>{/if}
{#if state.canTrash}<ui-button size="sm" variant="ghost" icon="trash" title="Move to trash (#)" onclick="fez.moveTo('trash')"></ui-button>{/if}
{#if state.canHome}<ui-button size="sm" variant="ghost" icon="tray" title={state.home === 'sent' ? 'Move to sent' : 'Move to inbox'} onclick="fez.moveTo('{state.home}')"></ui-button>{/if}
{#if state.canDelete}<ui-button size="sm" variant="ghost" icon="x-circle" title="Delete forever" onclick="fez.deleteForever()"></ui-button>{/if}
<ui-button size="sm" variant="ghost" icon="envelope-simple" title="Mark unread (Shift+U)" onclick="fez.markUnread()"></ui-button>
<ui-button size="sm" variant="ghost" icon="printer" title="Print (p)" loading={state.printing} onclick="fez.print()"></ui-button>
</div>
<h1 class="read-subject">{state.subject || '(no subject)'}</h1>
<div class="read-sub">
{#if state.folder}<span class="read-folder">{state.folder}</span>{/if}
{#if state.countLabel}<span>{state.countLabel}</span>{/if}
<span class="read-grow"></span>
<mail-team-bar key="bar-{state.team?.thread}" container={props.container} mailbox={props.mailbox || ''} :team="state.team" :people="state.people" :labels="props.labels" :on_team="(view) => fez.setTeam(view)" :on_labels="() => fez.state.on_labels()"></mail-team-bar>
</div>
{#if state.waiting}
<section class="ui-card pad-md read-screen" key="screen" fez:in="slide, duration=180">
<div class="read-screen-text">
<ui-icon name="user-plus" size="18"></ui-icon>
<span><strong class="read-screen-who">{state.screenWho}</strong> wrote for the first time. Where should their mail go?</span>
</div>
<div class="read-screen-actions">
<ui-button size="sm" icon="tray" label="Inbox" title="Mail you answer" :disabled="state.screening" onclick="fez.screen('inbox')"></ui-button>
<ui-button size="sm" icon="newspaper" label="Feed" title="Newsletters to read when you like" :disabled="state.screening" onclick="fez.screen('feed')"></ui-button>
<ui-button size="sm" icon="receipt" label="Paper Trail" title="Receipts and notices to keep" :disabled="state.screening" onclick="fez.screen('papertrail')"></ui-button>
<ui-button size="sm" icon="warning-octagon" label="Spam" title="Their mail goes to Spam" :disabled="state.screening" onclick="fez.screen('spam')"></ui-button>
</div>
</section>
{/if}
<div class="read-thread">
{#each state.entries as entry}
{#if entry.fold}
<button type="button" class="read-fold" key="fold" onclick="fez.unfold()">{entry.count} more message{entry.count === 1 ? '' : 's'}</button>
{:else}
<mail-message key={entry.item.id} container={props.container} mailbox={props.mailbox} :email="entry.item" :full="entry.full" :open="entry.open" :addresses="props.addresses" :sources="props.sources" :can_reply="state.canReply" :on_toggle="(id) => fez.toggle(id)" :on_star="(id) => fez.toggleStar(id)" :on_respond="(kind, id) => fez.respond(kind, id)" :on_changed="() => fez.load(true)" :on_images="(id) => fez.reloadSender(id)"></mail-message>
{/if}
{/each}
</div>
<mail-team-log key="log-{state.team?.thread}" container={props.container} mailbox={props.mailbox || ''} :team="state.team" :people="state.people" :on_team="(view) => fez.setTeam(view)"></mail-team-log>
{#if state.reply}
<section class="ui-card pad-lg read-compose" key="reply-{state.reply.kind}-{state.reply.for}" fez:in="fly, y=16, duration=220">
<div class="read-compose-head">
<h2 class="read-compose-title">{state.replyTitle}</h2>
</div>
<div fez:keep="reply-{state.reply.kind}-{state.reply.for}">
<mail-compose container={props.container} :mailbox="state.reply.mailbox" :mailboxes="props.senders.mailboxes" :domains="props.senders.domains" :domain="state.reply.domain" :from_local="state.reply.from_local" :to="state.reply.to" :cc="state.reply.cc" :subject="state.reply.subject" :body="state.reply.body" :attachments="state.reply.attachments" :in_reply_to="state.reply.in_reply_to" autofocus :on_done="(done) => fez.replyDone(done)"></mail-compose>
</div>
</section>
{:else if state.canReply}
<div class="read-respond">
<ui-button icon="arrow-bend-up-left" label="Reply" onclick="fez.respond('reply')"></ui-button>
{#if state.manyRecipients}<ui-button icon="arrow-bend-double-up-left" label="Reply all" onclick="fez.respond('all')"></ui-button>{/if}
<ui-button icon="arrow-bend-up-right" label="Forward" onclick="fez.respond('forward')"></ui-button>
</div>
{/if}
</div>
{/if}`
};
}

Fez('mail-reader', __fez_component_0);

export default __fez_component_0;
